import React, { useEffect } from "react";

import { styled } from "@compiled/react";
import { useIntl } from "react-intl";

import { Stack, Inline } from "@atlaskit/primitives-next";

import { useBaseIdentityAttrs } from "@/components/fastshift/analytics";
import { SourceKey } from "@/components/fastshift/analytics/constants";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/hook";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { GenericErrorDisplay } from "../../../common/displays";
import {
    ControlsPayload,
    DEFAULT_CONTROLS_PAYLOAD,
    FastshiftTable,
    OnSetControlsPayload
} from "../../../common/fastshift-table";
import { LegalBanner } from "../../../common/legal-footer/src/legal-banner";
import { getCurrentTime } from "../../../common/utils";
import { PartnerWelcomeBanner } from "../../../common/welcome-banner";
import {
    getCustomerDomains,
    setSelectionTableControls,
    useFastshiftDispatch,
    useFastshiftSelector
} from "../../../state";

import { useCustomerSelectionPageAnalytics } from "./hooks";
import { messages } from "./messages";
import { useDomainSelectionTable } from "./use-domain-selection-table";

const PageContainer = styled.div({
    width: "100%",
    maxWidth: "1120px"
});

export const FastshiftCustomerSelectionPage: React.FC = () => {
    const userInfo = useLoggedInUser();
    const currentTime = getCurrentTime();

    const dispatch = useFastshiftDispatch();
    const stargateClient = useBxpWebStargateClient();
    const intl = useIntl();

    const { data: customerDomains } = useFastshiftSelector(
        (state) => state.customerDomains
    );

    const { selectionTableControls } = useFastshiftSelector(
        (state) => state.pageStateCache
    );

    useEffect(() => {
        if (!customerDomains || customerDomains.data.length === 0) {
            dispatch(
                getCustomerDomains({
                    stargateClient,
                    controls: selectionTableControls
                })
            );
        }
        // we only want this effect to run once on page load once stargate client is available
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, stargateClient]);

    const onSetControlsPayload: OnSetControlsPayload = (
        controls: ControlsPayload
    ) => {
        dispatch(
            setSelectionTableControls({ selectionTableControls: controls })
        );
        dispatch(
            getCustomerDomains({
                stargateClient,
                controls
            })
        );
    };

    useCustomerSelectionPageAnalytics();
    const identityAttrs = useBaseIdentityAttrs();

    const { isTableLoading, isTableError, rows, head, pagination } =
        useDomainSelectionTable();

    const ErrorComponent = (
        <GenericErrorDisplay
            customMessage={intl.formatMessage(messages.errorMessage)}
            onRefresh={() => {
                dispatch(
                    getCustomerDomains({
                        stargateClient,
                        controls: DEFAULT_CONTROLS_PAYLOAD
                    })
                );
            }}
        />
    );

    return (
        <Inline alignInline="center">
            <PageContainer>
                <Stack space="space.400">
                    <Stack space="space.300">
                        <PartnerWelcomeBanner
                            currentTime={currentTime}
                            userName={userInfo?.name}
                        />
                        <LegalBanner />
                    </Stack>

                    <FastshiftTable
                        id="customer-selection-table"
                        enableSearch={true}
                        summaryText={intl.formatMessage(messages.summaryText, {
                            viewSize: rows.length,
                            totalNumber: pagination.totalElements
                        })}
                        isTableLoading={isTableLoading}
                        isTableError={isTableError}
                        errorComponent={ErrorComponent}
                        head={head}
                        rows={rows}
                        controls={{
                            onSetControlsPayload,
                            controls: selectionTableControls,
                            pagination
                        }}
                        analyticsSource={
                            SourceKey.FASTSHIFT_PORTAL_SELECTION_SCREEN
                        }
                        analyticsIdentity={identityAttrs}
                    />
                </Stack>
            </PageContainer>
        </Inline>
    );
};
